/// <reference types="vite/client" />

import { bbox } from "@turf/bbox";
import { circle } from "@turf/circle";
import { debugSelection } from "./debug.ts";
import { assertValidLngLatBounds } from "./geo/bounds.ts";
import type { LngLat, LngLatBounds } from "./geo/types.ts";
import { runtimeConfig } from "../config.ts";
import type { PoiCategory } from "./search/categories.ts";
import {
  isOsmSpatialRank,
  OSM_SPATIAL_RANK,
  type OsmLookupElement,
  type OsmSpatialRank,
} from "./osm/spatial.ts";
import type {
  LocatedOsmElement,
  OsmElement,
  OsmElementType,
  OsmElementWithTags,
  OsmTags,
} from "./osm/types.ts";

interface PostpassResponse {
  result: Array<PostpassRow>;
}

interface PostpassRow {
  osm_id: unknown;
  osm_type_str: unknown;
  tags: unknown;
  spatial_rank?: unknown;
  anchor_lng: unknown;
  anchor_lat: unknown;
}

// Radius around the clicked coordinate used to build the WGS84 bbox prefilter
// for both unbiased and name-hint candidate queries.
const LOOKUP_RADIUS_METERS = 20;

// Maximum rows returned by the unbiased query. These candidates power both the
// primary match and OSM-backed "Also here" alternatives.
const LOOKUP_LIMIT = 20;

// Maximum extra rows returned by the additive exact-name query. This keeps
// named vector-tile clicks from being starved by the unbiased query limit.
const NAME_HINT_LOOKUP_LIMIT = 8;

const POSTPASS_API_URL = runtimeConfig.postpassApiUrl;

export interface PoiCategorySearchArea {
  center: LngLat;
  bounds: LngLatBounds;
}

// Fetches nearby OSM candidates for coordinate-based selection, such as map
// clicks, long presses, and "What's here?". Final best-match selection stays in
// osm.ts so spatial evidence and tag scoring remain testable outside PostPass.
export async function fetchNearbyOsmCandidates(
  lat: number,
  lng: number,
  nameHint?: string,
  signal?: AbortSignal,
): Promise<OsmLookupElement[]> {
  const queries = [
    {
      kind: "unbiased",
      sql: buildQuery(lat, lng),
    },
  ];
  const normalizedNameHint = normalizeNameHint(nameHint);
  if (normalizedNameHint) {
    queries.push({
      kind: "name_hint",
      sql: buildNameHintQuery(lat, lng, normalizedNameHint),
    });
  }

  debugSelection("postpass request", () => ({
    url: POSTPASS_API_URL,
    clicked: { lat, lng },
    nameHint,
    decision: {
      lookupRadiusMeters: LOOKUP_RADIUS_METERS,
      limit: LOOKUP_LIMIT,
      nameHintLimit: normalizedNameHint ? NAME_HINT_LOOKUP_LIMIT : 0,
    },
    queries,
  }));

  const responses = await Promise.all(
    queries.map(async ({ kind, sql }) => ({
      kind,
      data: await fetchPostpass(sql, signal),
    })),
  );
  const elements = dedupeElements(
    responses.flatMap(({ data }) => parseOsmElements(data)),
  );

  debugSelection("postpass response", () => ({
    rawResultCount: responses.reduce(
      (total, response) => total + response.data.result.length,
      0,
    ),
    queryResults: responses.map(({ kind, data }) => ({
      kind,
      rawResultCount: data.result.length,
    })),
    elements: elements,
  }));

  return elements;
}

export async function fetchOsmElement(
  type: OsmElementType,
  id: number,
  lngLat: LngLat,
  signal?: AbortSignal,
): Promise<LocatedOsmElement | undefined> {
  const sql = buildExactElementQuery(type, id, lngLat);

  debugSelection("postpass exact osm element request", () => ({
    url: POSTPASS_API_URL,
    target: { type, id, lngLat },
    sql,
  }));

  const data = await fetchPostpass(sql, signal);
  const element = dedupeElements(parseOsmElements(data))[0];

  debugSelection("postpass exact osm element response", () => ({
    rawResultCount: data.result.length,
    element,
  }));

  return element
    ? {
      type: element.type,
      id: element.id,
      tags: element.tags,
      lnglat: element.lnglat,
    }
    : undefined;
}

export async function fetchCategoryOsmElements(
  category: PoiCategory,
  area: PoiCategorySearchArea,
  limit: number,
  signal?: AbortSignal,
): Promise<LocatedOsmElement[]> {
  const sql = buildCategoryQuery(category, area, limit);

  debugSelection("postpass poi category request", () => ({
    url: POSTPASS_API_URL,
    category: category.id,
    area,
    limit,
    sql,
  }));

  const data = await fetchPostpass(sql, signal);
  const elements = dedupeElements(parseLocatedOsmElements(data));

  debugSelection("postpass poi category response", () => ({
    rawResultCount: data.result.length,
    category: category.id,
    resultCount: elements.length,
    elements,
  }));

  return elements;
}

async function fetchPostpass(
  query: string,
  signal?: AbortSignal,
): Promise<PostpassResponse> {
  const body = new URLSearchParams();
  body.set("data", query);
  body.set("options[geojson]", "false");

  const resp = await fetch(POSTPASS_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
    signal,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<PostpassResponse>;
}

function bboxEnvelopeAroundPoint(
  lat: number,
  lng: number,
  radiusMeters: number,
): string {
  assertValidCoordinates(lat, lng);

  // ADR-008 documents why the bbox remains the broad index prefilter while
  // query builders add exact geometry predicates before local scoring.
  let [west, south, east, north] = bbox(
    circle([lng, lat], radiusMeters, { units: "meters" }),
  );

  if (west < -180 || east > 180) {
    west = -180;
    east = 180;
  }

  return `ST_MakeEnvelope(${west.toFixed(6)}, ${
    clampLatitude(south).toFixed(6)
  }, ${east.toFixed(6)}, ${clampLatitude(north).toFixed(6)}, 4326)`;
}

function bboxEnvelope(bounds: LngLatBounds): string {
  assertValidLngLatBounds(bounds, "Invalid category search bounds");
  return `ST_MakeEnvelope(${bounds.west.toFixed(6)}, ${
    bounds.south.toFixed(6)
  }, ${bounds.east.toFixed(6)}, ${bounds.north.toFixed(6)}, 4326)`;
}

function buildQuery(lat: number, lng: number): string {
  // A note about building SQL queries by string concatenation:
  // This builder keeps table names and limits fixed, validates coordinates, and
  // formats them as numbers. The security boundary is the PostPass service, not
  // this client code; it must treat callers as able to submit arbitrary SQL and
  // rely on read-only DB permissions plus query limits.
  const envelope = bboxEnvelopeAroundPoint(lat, lng, LOOKUP_RADIUS_METERS);
  const point = clickedPoint(lng, lat);
  // Relevance comes from OSM tags here, not the clicked vector-tile name. Exact
  // name matches are fetched additively by `buildNameHintQuery`, leaving this
  // query to preserve a broad candidate pool for "also here"; see ADR-008.
  const featureRank = featureRankSql();

  // PostPass stores OSM geometry classes in separate point/line/polygon tables,
  // so the query unions all three and normalizes their OSM element type into the
  // shape expected by the app. Some line/polygon rows represent relations, which
  // is why non-point tables inspect `osm_type` instead of assuming "way".
  const wayTypeExpr = "CASE WHEN osm_type = 'R' THEN 'relation' ELSE 'way' END";

  return [
    `WITH clicked AS (SELECT ${point} AS point, ${envelope} AS bbox)`,
    candidateRow(
      "postpass_point",
      "'node'",
      featureRank,
      OSM_SPATIAL_RANK.POINT_IN_LOOKUP_BBOX,
      "WHERE geom && clicked.bbox AND ST_Intersects(geom, clicked.bbox)",
    ),
    "UNION ALL",
    candidateRow(
      "postpass_line",
      wayTypeExpr,
      featureRank,
      OSM_SPATIAL_RANK.LINE_INTERSECTS_LOOKUP_BBOX,
      "WHERE geom && clicked.bbox AND ST_Intersects(geom, clicked.bbox)",
    ),
    "UNION ALL",
    candidateRow(
      "postpass_polygon",
      wayTypeExpr,
      featureRank,
      OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK,
      "WHERE geom && clicked.bbox AND ST_Covers(geom, clicked.point)",
    ),
    "ORDER BY feature_rank ASC, osm_id ASC",
    `LIMIT ${LOOKUP_LIMIT}`,
  ].join("\n");
}

function buildNameHintQuery(
  lat: number,
  lng: number,
  normalizedNameHint: string,
): string {
  const escapedName = normalizedNameHint.replace(/'/g, "''");
  const envelope = bboxEnvelopeAroundPoint(lat, lng, LOOKUP_RADIUS_METERS);
  const point = clickedPoint(lng, lat);
  const featureRank = featureRankSql();
  const nameMatch =
    `lower(tags->>'name') = '${escapedName}' OR lower(tags->>'name:en') = '${escapedName}' OR lower(tags->>'official_name') = '${escapedName}' OR lower(tags->>'alt_name') = '${escapedName}' OR lower(tags->>'short_name') = '${escapedName}'`;
  const wayTypeExpr = "CASE WHEN osm_type = 'R' THEN 'relation' ELSE 'way' END";

  // This query is additive to the unbiased candidate pool. ADR-008 explains why
  // named primary matching and OSM-backed alternatives need separate pools.
  return [
    `WITH clicked AS (SELECT ${point} AS point, ${envelope} AS bbox)`,
    candidateRow(
      "postpass_point",
      "'node'",
      featureRank,
      OSM_SPATIAL_RANK.POINT_IN_LOOKUP_BBOX,
      `WHERE geom && clicked.bbox AND ST_Intersects(geom, clicked.bbox) AND (${nameMatch})`,
    ),
    "UNION ALL",
    candidateRow(
      "postpass_line",
      wayTypeExpr,
      featureRank,
      OSM_SPATIAL_RANK.LINE_INTERSECTS_LOOKUP_BBOX,
      `WHERE geom && clicked.bbox AND ST_Intersects(geom, clicked.bbox) AND (${nameMatch})`,
    ),
    "UNION ALL",
    candidateRow(
      "postpass_polygon",
      wayTypeExpr,
      featureRank,
      OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK,
      `WHERE geom && clicked.bbox AND ST_Covers(geom, clicked.point) AND (${nameMatch})`,
    ),
    "ORDER BY feature_rank ASC, osm_id ASC",
    `LIMIT ${NAME_HINT_LOOKUP_LIMIT}`,
  ].join("\n");
}

function buildExactElementQuery(
  type: OsmElementType,
  idValue: number,
  lngLat: LngLat,
): string {
  const id = osmIdLiteral(idValue);
  const [lng, lat] = lngLat;
  // Live PostPass checks showed id-only exact queries can take tens of seconds,
  // especially misses in line tables. The spatial predicate gives PostGIS an
  // indexed bbox prefilter while the OSM id remains the exact identity check.
  // This assumes target.lngLat comes from the selected element or a reliable
  // search result near that element. Large polygons and long ways still match
  // when their bbox intersects this envelope; a bad coordinate outside the
  // element bbox should fail instead of silently falling back to nearby ranking.
  const envelope = bboxEnvelopeAroundPoint(lat, lng, LOOKUP_RADIUS_METERS);

  switch (type) {
    case "node":
      return exactElementQuery([
        exactElementRow(
          "postpass_point",
          "'node'",
          OSM_SPATIAL_RANK.POINT_IN_LOOKUP_BBOX,
          `WHERE geom && ${envelope} AND osm_id = ${id}`,
        ),
      ]);
    case "way":
      return exactElementQuery([
        exactElementRow(
          "postpass_polygon",
          "'way'",
          OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK,
          `WHERE geom && ${envelope} AND osm_id = ${id} AND osm_type IS DISTINCT FROM 'R'`,
        ),
        exactElementRow(
          "postpass_line",
          "'way'",
          OSM_SPATIAL_RANK.LINE_INTERSECTS_LOOKUP_BBOX,
          `WHERE geom && ${envelope} AND osm_id = ${id} AND osm_type IS DISTINCT FROM 'R'`,
        ),
      ]);
    case "relation":
      return exactElementQuery([
        exactElementRow(
          "postpass_polygon",
          "'relation'",
          OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK,
          `WHERE geom && ${envelope} AND osm_id = ${id} AND osm_type = 'R'`,
        ),
        exactElementRow(
          "postpass_line",
          "'relation'",
          OSM_SPATIAL_RANK.LINE_INTERSECTS_LOOKUP_BBOX,
          `WHERE geom && ${envelope} AND osm_id = ${id} AND osm_type = 'R'`,
        ),
      ]);
  }
}

function buildCategoryQuery(
  category: PoiCategory,
  area: PoiCategorySearchArea,
  limit: number,
): string {
  const [lng, lat] = area.center;
  const envelope = bboxEnvelope(area.bounds);
  const point = clickedPoint(lng, lat);
  const tagFilter = tagFilterSql(category);
  const limitValue = limitLiteral(limit);
  const wayTypeExpr = "CASE WHEN osm_type = 'R' THEN 'relation' ELSE 'way' END";

  return [
    `WITH search_area AS (SELECT ${point} AS center, ${envelope} AS bbox)`,
    categoryCandidateRow(
      "postpass_point",
      "'node'",
      `WHERE geom && search_area.bbox AND ST_Intersects(geom, search_area.bbox) AND (${tagFilter})`,
    ),
    "UNION ALL",
    categoryCandidateRow(
      "postpass_line",
      wayTypeExpr,
      `WHERE geom && search_area.bbox AND ST_Intersects(geom, search_area.bbox) AND (${tagFilter})`,
    ),
    "UNION ALL",
    categoryCandidateRow(
      "postpass_polygon",
      wayTypeExpr,
      `WHERE geom && search_area.bbox AND ST_Intersects(geom, search_area.bbox) AND (${tagFilter})`,
    ),
    `LIMIT ${limitValue}`,
  ].join("\n");
}

function clickedPoint(lng: number, lat: number): string {
  return `ST_SetSRID(ST_MakePoint(${lng.toFixed(6)}, ${lat.toFixed(6)}), 4326)`;
}

function candidateRow(
  table: string,
  typeExpr: string,
  featureRank: string,
  spatialRank: OsmSpatialRank,
  whereClause: string,
): string {
  return `SELECT osm_id, ${typeExpr} AS osm_type_str, tags, ${featureRank} AS feature_rank, ${spatialRank} AS spatial_rank, ST_X(ST_PointOnSurface(geom)) AS anchor_lng, ST_Y(ST_PointOnSurface(geom)) AS anchor_lat FROM ${table}, clicked\n${whereClause}`;
}

function exactElementQuery(selects: string[]): string {
  return [
    selects.join("\nUNION ALL\n"),
    "ORDER BY spatial_rank ASC",
    "LIMIT 1",
  ].join("\n");
}

function exactElementRow(
  table: string,
  typeExpr: string,
  spatialRank: OsmSpatialRank,
  whereClause: string,
): string {
  return `SELECT osm_id, ${typeExpr} AS osm_type_str, tags, ${spatialRank} AS spatial_rank, ST_X(ST_PointOnSurface(geom)) AS anchor_lng, ST_Y(ST_PointOnSurface(geom)) AS anchor_lat FROM ${table}\n${whereClause}`;
}

function categoryCandidateRow(
  table: string,
  typeExpr: string,
  whereClause: string,
): string {
  return `SELECT osm_id, ${typeExpr} AS osm_type_str, tags, ST_X(ST_PointOnSurface(geom)) AS anchor_lng, ST_Y(ST_PointOnSurface(geom)) AS anchor_lat FROM ${table}, search_area\n${whereClause}`;
}

function featureRankSql(): string {
  const boundaryLike =
    "tags->>'boundary' IS NOT NULL OR tags->>'disused:boundary' IS NOT NULL OR tags->>'abandoned:boundary' IS NOT NULL OR tags->>'historic:boundary' IS NOT NULL OR tags->>'was:boundary' IS NOT NULL";
  return `CASE WHEN ${boundaryLike} THEN 9 WHEN tags->>'route' IS NOT NULL THEN 8 WHEN tags->>'building' IS NOT NULL OR tags->>'amenity' IS NOT NULL OR tags->>'shop' IS NOT NULL OR tags->>'tourism' IS NOT NULL OR tags->>'leisure' IS NOT NULL OR tags->>'historic' IS NOT NULL OR tags->>'office' IS NOT NULL OR tags->>'craft' IS NOT NULL THEN 0 WHEN tags->>'name' IS NOT NULL OR tags->>'name:en' IS NOT NULL THEN 2 ELSE 3 END`;
}

function parseOsmElements(data: PostpassResponse): OsmLookupElement[] {
  return data.result.map((row) => ({
    ...parseOsmIdentity(row),
    spatialRank: osmSpatialRank(row.spatial_rank),
    lnglat: osmAnchor(row),
  }));
}

function parseLocatedOsmElements(data: PostpassResponse): LocatedOsmElement[] {
  return data.result.map((row) => ({
    ...parseOsmIdentity(row),
    lnglat: osmAnchor(row),
  }));
}

function parseOsmIdentity(
  row: PostpassResponse["result"][number],
): OsmElementWithTags {
  return {
    type: osmElementType(row.osm_type_str),
    id: osmId(row.osm_id),
    tags: osmTags(row.tags),
  };
}

function osmElementType(value: unknown): OsmElementType {
  if (value === "node" || value === "way" || value === "relation") {
    return value;
  }
  throw new Error(`Unexpected PostPass OSM type: ${value}`);
}

function osmId(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new Error(`Unexpected PostPass OSM id: ${value}`);
}

function osmIdLiteral(value: number): string {
  if (Number.isSafeInteger(value) && value > 0) return String(value);
  throw new RangeError(`Invalid OSM id: ${value}`);
}

function osmTags(tags: unknown): OsmTags {
  if (!tags || typeof tags !== "object" || Array.isArray(tags)) {
    throw new Error(`Unexpected PostPass tags: ${tags}`);
  }
  for (const [key, value] of Object.entries(tags)) {
    if (typeof value !== "string") {
      throw new Error(`Unexpected PostPass tag ${key}: ${value}`);
    }
  }
  return tags as OsmTags;
}

function osmSpatialRank(value: unknown): OsmSpatialRank {
  if (typeof value !== "number" || !isOsmSpatialRank(value)) {
    throw new Error(`Unexpected PostPass spatial rank: ${value}`);
  }

  return value;
}

function osmAnchor(row: PostpassResponse["result"][number]): LngLat {
  const { anchor_lng, anchor_lat } = row;
  if (
    typeof anchor_lng === "number" && Number.isFinite(anchor_lng) &&
    typeof anchor_lat === "number" && Number.isFinite(anchor_lat)
  ) {
    return [anchor_lng, anchor_lat];
  }
  throw new Error(`Unexpected PostPass anchor: ${anchor_lng}, ${anchor_lat}`);
}

function dedupeElements<T extends OsmElement>(
  elements: T[],
): T[] {
  const seen = new Set<string>();
  return elements.filter((element) => {
    const key = `${element.type}:${element.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeNameHint(value: string | undefined): string {
  return value?.trim().toLocaleLowerCase() ?? "";
}

function tagFilterSql(category: PoiCategory): string {
  if (category.tagFilters.length === 0) {
    throw new RangeError(
      `Category ${category.id} must define at least one tag filter`,
    );
  }

  return category.tagFilters.map(({ key, value }) =>
    `tags->>${sqlStringLiteral(key)} = ${sqlStringLiteral(value)}`
  ).join(" OR ");
}

function sqlStringLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

function limitLiteral(value: number): string {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`Invalid category search limit: ${value}`);
  }
  return String(value);
}

function clampLatitude(value: number): number {
  return Math.max(-90, Math.min(90, value));
}

function assertValidCoordinates(lat: number, lng: number): void {
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    throw new RangeError(`Invalid latitude: ${lat}`);
  }

  if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
    throw new RangeError(`Invalid longitude: ${lng}`);
  }
}
